// Group CRUD, membership and invite-link edge calls (ticket 302), reconciled
// against the real apps/edge/src/groups.ts route table and @proef/contract.
import { z } from "zod";
import type { Page } from "../api";
import { call } from "../edgeApi";
import { BanGroupMemberRequest, GroupBanList } from "@proef/contract";
import {
  CreateGroupRequest,
  GroupJoinRequestList,
  GroupList,
  GroupMemberList,
  GroupMemberView,
  GroupPreview,
  type GroupRole,
  GroupSearchResponse,
  GroupView,
  RequestToJoinGroupResponse,
  UpdateGroupRequest,
} from "./group";

/** Every "ok" action route (`/leave`, `/members/:id/remove`, `DELETE /v1/groups/:id`) replies
 * `{ ok: true }`, never a bare 204/void -- matches apps/edge/src/groups.ts's actual responses. */
const OkResponse = z.object({ ok: z.literal(true) });

function post(body: unknown): RequestInit {
  return { method: "POST", body: JSON.stringify(body) };
}
function patch(body: unknown): RequestInit {
  return { method: "PATCH", body: JSON.stringify(body) };
}

export function createGroup(req: z.input<typeof CreateGroupRequest>): Promise<GroupView> {
  return call(GroupView, "/v1/groups", post(CreateGroupRequest.parse(req)));
}

export function getGroup(groupId: string): Promise<GroupView> {
  return call(GroupView, `/v1/groups/${encodeURIComponent(groupId)}`);
}

/** Per-caller cursor state for `listMyGroups`'s offset-to-cursor adapter below -- same pattern as
 * `memberCursors`: GET /v1/groups is genuinely cursor-paginated server-side, usePaged.ts drives it
 * by offset, so one cursor is cached and advanced on each sequential call from 0. */
let myGroupsCursor: string | undefined;

/**
 * Groups screen's own list -- never a public/discoverable listing, this is
 * the viewer's own groups only (whoCanView doesn't gate this route at all).
 * Adapts GET /v1/groups's cursor-paginated wire response to usePaged.ts's
 * `Page<T>` shape, mirroring `listGroupMembers` below.
 */
export async function listMyGroups(_limit = 50, offset = 0): Promise<Page<GroupView>> {
  const cursor = offset === 0 ? undefined : myGroupsCursor;
  const query = cursor === undefined ? "" : `?cursor=${encodeURIComponent(cursor)}`;
  const response = await call(GroupList, `/v1/groups${query}`);
  myGroupsCursor = response.nextCursor ?? undefined;
  const total = offset + response.items.length + (response.nextCursor === null ? 0 : 1);
  return { items: response.items, total };
}

export function updateGroup(
  groupId: string,
  req: z.input<typeof UpdateGroupRequest>,
): Promise<GroupView> {
  return call(
    GroupView,
    `/v1/groups/${encodeURIComponent(groupId)}`,
    patch(UpdateGroupRequest.parse(req)),
  );
}

export async function deleteGroup(groupId: string): Promise<void> {
  await call(OkResponse, `/v1/groups/${encodeURIComponent(groupId)}`, { method: "DELETE" });
}

export async function leaveGroup(groupId: string): Promise<void> {
  await call(OkResponse, `/v1/groups/${encodeURIComponent(groupId)}/leave`, post({}));
}

/** `POST .../members/:userId/remove`, not `DELETE .../members/:userId` -- the real route
 * (apps/edge/src/groups.ts) only ever accepts a POST here. */
export async function removeGroupMember(groupId: string, userId: string): Promise<void> {
  await call(
    OkResponse,
    `/v1/groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(userId)}/remove`,
    post({}),
  );
}

/** `POST .../members/:userId/role`: only an owner may, and never so that no owner is left (409). */
export function changeMemberRole(
  groupId: string,
  userId: string,
  role: GroupRole,
): Promise<GroupMemberView> {
  return call(
    GroupMemberView,
    `/v1/groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(userId)}/role`,
    post({ role }),
  );
}

/** Ends the membership and keeps the person out of every link, code and request until lifted. */
export async function banGroupMember(groupId: string, userId: string): Promise<void> {
  await call(
    OkResponse,
    `/v1/groups/${encodeURIComponent(groupId)}/bans`,
    post(BanGroupMemberRequest.parse({ userId })),
  );
}

export async function unbanGroupMember(groupId: string, userId: string): Promise<void> {
  await call(
    OkResponse,
    `/v1/groups/${encodeURIComponent(groupId)}/bans/${encodeURIComponent(userId)}`,
    { method: "DELETE" },
  );
}

export async function listGroupBans(groupId: string): Promise<GroupBanList["items"]> {
  return (await call(GroupBanList, `/v1/groups/${encodeURIComponent(groupId)}/bans`)).items;
}

/** Per-group cursor state for `listGroupMembers`'s offset-to-cursor adapter below -- GET
 * /v1/groups/:id/members is genuinely cursor-paginated server-side (`GroupMemberList`'s
 * `nextCursor`), while usePaged.ts drives every paged list by offset (`Page<T>`'s `total`,
 * `load(items.length)`). Caching the last cursor per group, rather than per offset, is enough
 * because usePaged.ts only ever calls a given list sequentially from 0 -- never out of order or
 * concurrently for the same key. */
const memberCursors = new Map<string, string>();

/** Adapts the cursor-paginated wire response to usePaged.ts's `Page<T>` shape: `total` is a
 * sentinel (`offset + items.length`, +1 when another page exists), not a real server count,
 * since the real route never returns one -- it only ever needs to satisfy usePaged's
 * `items.length < total` "show more" check, never to display an exact count. */
export async function listGroupMembers(
  groupId: string,
  _limit = 50,
  offset = 0,
): Promise<Page<GroupMemberView>> {
  const cursor = offset === 0 ? undefined : memberCursors.get(groupId);
  const query = cursor === undefined ? "" : `?cursor=${encodeURIComponent(cursor)}`;
  const response = await call(
    GroupMemberList,
    `/v1/groups/${encodeURIComponent(groupId)}/members${query}`,
  );
  if (response.nextCursor === null) memberCursors.delete(groupId);
  else memberCursors.set(groupId, response.nextCursor);
  const total = offset + response.items.length + (response.nextCursor === null ? 0 : 1);
  return { items: response.items, total };
}

/**
 * GroupDirectory's search box -- discoverable group directory (human override #3):
 * GET /v1/groups/search, substring match on group name, scoped server-side to
 * `whoCanView: "discoverable"` groups only. Cursor-paginated, same shape as
 * friendsApi.searchFriendCandidates.
 */
export function searchGroups(query: string, cursor?: string): Promise<GroupSearchResponse> {
  const params = new URLSearchParams({ q: query });
  if (cursor !== undefined) params.set("cursor", cursor);
  return call(GroupSearchResponse, `/v1/groups/search?${params.toString()}`);
}

/** GroupDirectoryPreview's fetch -- the non-member identity-confirmation view of a discoverable
 * group found via search, never the roster (apps/edge/src/groups.ts's GET /v1/groups/:id/preview). */
export function getGroupPreview(groupId: string): Promise<GroupPreview> {
  return call(GroupPreview, `/v1/groups/${encodeURIComponent(groupId)}/preview`);
}

/** GroupDirectoryPreview's "request to join" action -- idempotent on repeat taps, creates a
 * `pending` group_members row an owner/admin must accept (groups.feature's directory rule). */
export function requestToJoinGroup(groupId: string): Promise<RequestToJoinGroupResponse> {
  return call(
    RequestToJoinGroupResponse,
    `/v1/groups/${encodeURIComponent(groupId)}/join-requests`,
    post({}),
  );
}

/** Per-group cursor state for `listGroupJoinRequests`'s offset-to-cursor adapter, same pattern
 * as `memberCursors` above. */
const joinRequestCursors = new Map<string, string>();

/** GroupDetail's "join requests" sub-section (owner/admin, discoverable groups only) -- cursor
 * paginated like `listGroupMembers`, adapted to usePaged.ts's `Page<T>` shape the same way. */
export async function listGroupJoinRequests(
  groupId: string,
  _limit = 50,
  offset = 0,
): Promise<Page<GroupJoinRequestList["items"][number]>> {
  const cursor = offset === 0 ? undefined : joinRequestCursors.get(groupId);
  const query = cursor === undefined ? "" : `?cursor=${encodeURIComponent(cursor)}`;
  const response = await call(
    GroupJoinRequestList,
    `/v1/groups/${encodeURIComponent(groupId)}/join-requests${query}`,
  );
  if (response.nextCursor === null) joinRequestCursors.delete(groupId);
  else joinRequestCursors.set(groupId, response.nextCursor);
  const total = offset + response.items.length + (response.nextCursor === null ? 0 : 1);
  return { items: response.items, total };
}

/** Owner/admin-only, matching the role matrix's other admin-gated actions. */
export function acceptGroupJoinRequest(groupId: string, userId: string): Promise<GroupMemberView> {
  return call(
    GroupMemberView,
    `/v1/groups/${encodeURIComponent(groupId)}/join-requests/${encodeURIComponent(userId)}/accept`,
    post({}),
  );
}

export async function declineGroupJoinRequest(groupId: string, userId: string): Promise<void> {
  await call(
    OkResponse,
    `/v1/groups/${encodeURIComponent(groupId)}/join-requests/${encodeURIComponent(userId)}/decline`,
    post({}),
  );
}

export type {
  GroupJoinRequestView,
  GroupMemberView,
  GroupPreview,
  GroupRole,
  GroupSearchResult,
  GroupView,
  WhoCanInvite,
  WhoCanView,
} from "./group";
